// src/entraAuth.js
//
// Entra ID sign-in. MSAL is used only to obtain an access token for the
// backend API; that token is exchanged once at POST /api/login/entra for the
// normal ff_session cookie, then MSAL's cache is cleared. Every other request
// keeps using the cookie session.
import { PublicClientApplication } from "@azure/msal-browser";
import { msalConfig, loginRequest } from "./authConfig";

export const entraEnabled = Boolean(
  process.env.REACT_APP_AAD_CLIENT_ID && process.env.REACT_APP_AAD_TENANT_ID
);

let msal = null;
// Result of returning from the Entra redirect: { token } or { error }.
// Consumed once by SignIn so a later sign-out doesn't replay it.
let pendingRedirect = null;

function msalErrorMessage(err, fallback) {
  return err?.errorMessage || err?.message || fallback;
}

// Call once before the first render. Processes the redirect back from Entra.
export async function initEntra() {
  if (!entraEnabled) return;
  try {
    msal = new PublicClientApplication(msalConfig);
    await msal.initialize();
    const result = await msal.handleRedirectPromise();
    if (result?.account) {
      const tokenResult = await msal.acquireTokenSilent({
        scopes: loginRequest.scopes,
        account: result.account,
      });
      pendingRedirect = { token: tokenResult.accessToken };
    }
  } catch (err) {
    pendingRedirect = { error: msalErrorMessage(err, "Microsoft sign-in failed") };
  }
}

export function takeRedirectResult() {
  const r = pendingRedirect;
  pendingRedirect = null;
  return r;
}

export function startEntraLogin() {
  if (!msal) {
    return Promise.reject(new Error("Microsoft sign-in is not available"));
  }
  return msal.loginRedirect({
    scopes: loginRequest.scopes,
    prompt: "select_account",
  });
}

// Drop MSAL's cached tokens once the backend session exists (or the
// exchange failed); the browser never needs them again.
export async function clearEntraCache() {
  if (!msal) return;
  try {
    await msal.clearCache();
  } catch {
    // best effort; sessionStorage is cleared when the tab closes anyway
  }
}
